// src/pages/EmployeeList.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { 
  collection, 
  getDocs, 
  onSnapshot,
  query, 
  orderBy,
  doc, 
  getDoc,
  updateDoc, 
  setDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { useAuthorizedAccess } from '../hooks/useAuthorizedAccess';
import { 
  FaUsers, 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaEdit, 
  FaUpload,
  FaFileAlt,
  FaLink,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaClipboardList
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import EmployeeChecklistModal from '../components/EmployeeChecklistModal';
import './EmployeeList.css';

const EmployeeList = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { hasAccess, loading: accessLoading } = useAuthorizedAccess();

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Default to active, options: all, active, resigned
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Checklist modal state
  const [checklistEmployee, setChecklistEmployee] = useState(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);

  // Auto-sync missing resigned employees from experience_records
  const autoSyncResignedEmployees = async () => {
    try {
      console.log('🔄 Checking for missing resigned employees...');
      
      // Fetch all experience records
      const experienceSnap = await getDocs(collection(db, 'experience_records'));
      const experienceRecords = experienceSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      let syncedCount = 0;
      
      // Check each experience record
      for (const expRecord of experienceRecords) {
        // Only process records with leaving dates (resigned employees)
        if (!expRecord.leavingDate) continue;
        
        const employeeId = expRecord.employeeId || expRecord.id;
        
        // Check if employee exists in employee_records_complete
        const empDocRef = doc(db, 'employee_records_complete', employeeId);
        const empDocSnap = await getDoc(empDocRef);
        
        if (!empDocSnap.exists()) {
          // Employee missing - create entry
          await setDoc(empDocRef, {
            employeeId: employeeId,
            name: expRecord.employeeName || 'Unknown',
            email: expRecord.email || '',
            phone: expRecord.phone || '',
            department: expRecord.department || '',
            position: expRecord.position || '',
            role: 'employee',
            photoURL: '',
            joiningDate: expRecord.joiningDate || '',
            leavingDate: expRecord.leavingDate,
            status: 'resigned',
            uid: expRecord.uid || '',
            
            // Bank & Statutory (empty for old records)
            location: '',
            bankName: '',
            bankAccountNumber: '',
            ifscCode: '',
            panNumber: '',
            pfNumber: '',
            uanNumber: '',
            grossMonthlySalary: '',
            
            // Salary History
            salaryHistory: {},
            lastSalaryUpdateMonth: null,
            lastSalaryUpdateDate: null,
            
            // Experience Letter flags
            hasExperienceLetter: true,
            experienceRecordId: employeeId,
            
            // Empty arrays
            documents: [],
            driveLinks: [],
            notes: [],
            
            // Metadata
            createdAt: serverTimestamp(),
            createdBy: 'auto-sync',
            updatedAt: serverTimestamp(),
            syncedAt: serverTimestamp()
          });
          
          syncedCount++;
          console.log(`✅ Auto-synced resigned employee: ${expRecord.employeeName}`);
        }
      }
      
      if (syncedCount > 0) {
        console.log(`✅ Auto-sync complete: ${syncedCount} resigned employees added to Employee List`);
        toast.success(`${syncedCount} resigned employee${syncedCount > 1 ? 's' : ''} auto-synced to Employee List`);
      } else {
        console.log('✅ No missing resigned employees found');
      }
    } catch (error) {
      console.error('❌ Auto-sync error:', error);
      // Don't show error toast - fail silently
    }
  };

  useEffect(() => {
    if (!hasAccess) return;
    autoSyncResignedEmployees(); // fire-and-forget migration check
    setLoading(true);
    const q = query(collection(db, 'employee_records_complete'), orderBy('joiningDate', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const employeeData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEmployees(employeeData);
      setFilteredEmployees(employeeData);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching employees:', err);
      toast.error('Failed to load employee data');
      setLoading(false);
    });
    return () => unsub();
  }, [hasAccess]);

  // Apply filters
  useEffect(() => {
    let filtered = [...employees];

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(emp => 
        emp.name?.toLowerCase().includes(search) ||
        emp.employeeId?.toLowerCase().includes(search) ||
        emp.email?.toLowerCase().includes(search) ||
        emp.department?.toLowerCase().includes(search) ||
        emp.position?.toLowerCase().includes(search)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(emp => emp.status === statusFilter);
    }

    // Department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(emp => emp.department === departmentFilter);
    }

    setFilteredEmployees(filtered);
  }, [searchTerm, statusFilter, departmentFilter, employees]);

  // Get unique departments for filter
  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];

  // Handle document upload
  const handleDocumentUpload = async (employeeId, docType, file) => {
    if (!file) return;

    setUploading(true);
    const toastId = toast.loading('Uploading document...');

    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `employee_documents/${employeeId}/${docType}_${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update Firestore
      const employeeRef = doc(db, 'employee_records_complete', employeeId);
      const updateData = {
        [`documents.${docType}`]: {
          url: downloadURL,
          fileName: file.name,
          uploadedAt: serverTimestamp(),
          uploadedBy: userData.uid,
          uploaderName: userData.name
        },
        updatedAt: serverTimestamp()
      };

      await updateDoc(employeeRef, updateData);
      
      toast.success('Document uploaded successfully!', { id: toastId });
      // onSnapshot will auto-refresh
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  // Handle drive link update
  const handleDriveLinkUpdate = async (employeeId, linkType, linkUrl) => {
    if (!linkUrl.trim()) return;

    try {
      const employeeRef = doc(db, 'employee_records_complete', employeeId);
      await updateDoc(employeeRef, {
        [`driveLinks.${linkType}`]: linkUrl.trim(),
        updatedAt: serverTimestamp()
      });

      toast.success('Drive link updated!');
      // onSnapshot will auto-refresh
    } catch (error) {
      console.error('Error updating link:', error);
      toast.error('Failed to update link');
    }
  };

  // Add note to employee
  const handleAddNote = async (employeeId, noteText) => {
    if (!noteText.trim()) return;

    try {
      const employee = employees.find(e => e.id === employeeId);
      const existingNotes = employee?.notes || [];

      const newNote = {
        text: noteText.trim(),
        addedAt: new Date().toISOString(),
        addedBy: userData.uid,
        addedByName: userData.name
      };

      const employeeRef = doc(db, 'employee_records_complete', employeeId);
      await updateDoc(employeeRef, {
        notes: [...existingNotes, newNote],
        updatedAt: serverTimestamp()
      });

      toast.success('Note added!');
      // onSnapshot will auto-refresh
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    }
  };

  // Loading states
  if (accessLoading) {
    return (
      <div className="loading-container">
        <FaSpinner className="spinner" />
        <p>Checking access permissions...</p>
      </div>
    );
  }

  // Access denied
  if (!hasAccess) {
    return (
      <div className="access-denied-container">
        <FaTimesCircle size={64} color="#e74c3c" />
        <h2>Access Denied</h2>
        <p>You don't have permission to view the Employee Directory page.</p>
        <p>Contact your administrator if you need access.</p>
      </div>
    );
  }

  return (
    <div className="employee-list-container">
      <div className="employee-list-header">
        <div className="header-title">
          <FaUsers size={32} />
          <div>
            <h1>Employee Directory</h1>
            <p>Comprehensive employee database with all records and documents</p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, ID, email, department, position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label><FaFilter /> Status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">Active Only</option>
              <option value="resigned">Resigned Only</option>
              <option value="all">All Employees</option>
            </select>
          </div>

          <div className="filter-group">
            <label><FaFilter /> Department:</label>
            <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>

          <div className="filter-stats">
            <span>Showing {filteredEmployees.length} of {employees.length} employees</span>
          </div>
        </div>
      </div>

      {/* Employee Table */}
      {loading ? (
        <div className="loading-state">
          <FaSpinner className="spinner" />
          <p>Loading employee data...</p>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className="empty-state">
          <FaUsers size={48} />
          <h3>No employees found</h3>
          <p>Try adjusting your filters or search terms</p>
        </div>
      ) : (
        <div className="employee-table-wrapper">
          <table className="employee-table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Department</th>
                <th>Position</th>
                <th>Joining Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees
                .filter(employee => {
                  // Only show superadmin row to superadmin users
                  if (employee.role === 'superadmin') {
                    return userData?.role === 'superadmin';
                  }
                  return true;
                })
                .map(employee => (
                  <tr key={employee.id} className={employee.role === 'superadmin' && userData?.role === 'superadmin' ? 'superadmin-row-highlight' : ''}>
                    <td data-label="ID">{employee.employeeId}</td>
                    <td data-label="Name">
                      <div className="employee-name-cell">
                        <img 
                          src={employee.photoURL || '/default-avatar.png'} 
                          alt={employee.name}
                          className="employee-avatar-small"
                        />
                        <span>{employee.name}</span>
                      </div>
                    </td>
                    <td data-label="Department">{employee.department || 'N/A'}</td>
                    <td data-label="Position">{employee.position || 'N/A'}</td>
                    <td data-label="Joining Date">{employee.joiningDate || 'N/A'}</td>
                    <td data-label="Status">
                      <span className={`status-badge ${employee.status || 'active'}`}>
                        {employee.status === 'resigned' ? (
                          <><FaTimesCircle /> Resigned</>
                        ) : (
                          <><FaCheckCircle /> Active</>
                        )}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <div className="action-btns-group">
                        <button
                          className="action-btn-primary"
                          onClick={() => navigate(`/dashboard/employee-detail/${employee.employeeId}`)}
                          data-tooltip="View Full Details"
                        >
                          <FaEye /> Details
                        </button>
                        <button
                          className="action-btn-checklist"
                          onClick={() => {
                            setChecklistEmployee(employee);
                            setShowChecklistModal(true);
                          }}
                          data-tooltip="Onboarding Checklist"
                        >
                          <FaClipboardList /> Checklist
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Checklist Modal */}
      {showChecklistModal && checklistEmployee && (
        <EmployeeChecklistModal
          employee={checklistEmployee}
          onClose={() => {
            setShowChecklistModal(false);
            setChecklistEmployee(null);
          }}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedEmployee && (
        <EmployeeDetailModal
          employee={selectedEmployee}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedEmployee(null);
          }}
          onUploadDocument={handleDocumentUpload}
          onUpdateDriveLink={handleDriveLinkUpdate}
          onAddNote={handleAddNote}
          uploading={uploading}
          currentUser={userData}
        />
      )}
    </div>
  );
};

// Separate Modal Component for Employee Details
const EmployeeDetailModal = ({ 
  employee, 
  onClose, 
  onUploadDocument, 
  onUpdateDriveLink, 
  onAddNote,
  uploading,
  currentUser
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [newNote, setNewNote] = useState('');
  const [driveLinkType, setDriveLinkType] = useState('personalFolder');
  const [driveLinkUrl, setDriveLinkUrl] = useState('');

  const documentTypes = [
    { key: 'offerLetter', label: 'Offer Letter' },
    { key: 'contract', label: 'Employment Contract' },
    { key: 'idProof', label: 'ID Proof (Aadhar/PAN)' },
    { key: 'educationCertificates', label: 'Education Certificates' },
    { key: 'bankDocuments', label: 'Bank Documents' },
    { key: 'other', label: 'Other Documents' }
  ];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content employee-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <img src={employee.photoURL || '/default-avatar.png'} alt={employee.name} className="employee-avatar" />
            <div>
              <h2>{employee.name}</h2>
              <p>{employee.position} • {employee.employeeId}</p>
            </div>
          </div>
          <button className="close-modal-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button 
            className={activeTab === 'basic' ? 'active' : ''} 
            onClick={() => setActiveTab('basic')}
          >
            Basic Info
          </button>
          <button 
            className={activeTab === 'documents' ? 'active' : ''} 
            onClick={() => setActiveTab('documents')}
          >
            <FaFileAlt /> Documents
          </button>
          <button 
            className={activeTab === 'links' ? 'active' : ''} 
            onClick={() => setActiveTab('links')}
          >
            <FaLink /> Drive Links
          </button>
          <button 
            className={activeTab === 'notes' ? 'active' : ''} 
            onClick={() => setActiveTab('notes')}
          >
            Notes
          </button>
        </div>

        <div className="modal-body">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="tab-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>Employee ID:</label>
                  <span>{employee.employeeId}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{employee.email}</span>
                </div>
                <div className="info-item">
                  <label>Phone:</label>
                  <span>{employee.phone || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Department:</label>
                  <span>{employee.department || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Position:</label>
                  <span>{employee.position || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Joining Date:</label>
                  <span>{employee.joiningDate || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Leaving Date:</label>
                  <span>{employee.leavingDate || 'Currently Active'}</span>
                </div>
                <div className="info-item">
                  <label>Status:</label>
                  <span className={`status-badge ${employee.status || 'active'}`}>
                    {employee.status === 'resigned' ? 'Resigned' : 'Active'}
                  </span>
                </div>
                <div className="info-item">
                  <label>Location:</label>
                  <span>{employee.location || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Bank Name:</label>
                  <span>{employee.bankName || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Bank Account:</label>
                  <span>{employee.bankAccountNumber || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>IFSC Code:</label>
                  <span>{employee.ifscCode || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>PAN Number:</label>
                  <span>{employee.panNumber || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>PF Number:</label>
                  <span>{employee.pfNumber || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>UAN:</label>
                  <span>{employee.uanNumber || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>Gross Monthly Salary:</label>
                  <span>{employee.grossMonthlySalary ? `₹${employee.grossMonthlySalary}` : 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="tab-content">
              <h3>Employee Documents</h3>
              <div className="documents-grid">
                {documentTypes.map(docType => (
                  <div key={docType.key} className="document-card">
                    <h4>{docType.label}</h4>
                    {employee.documents?.[docType.key] ? (
                      <div className="document-info">
                        <p><strong>File:</strong> {employee.documents[docType.key].fileName}</p>
                        <p><strong>Uploaded:</strong> {employee.documents[docType.key].uploadedAt?.toDate?.()?.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) || 'N/A'}</p>
                        <p><strong>By:</strong> {employee.documents[docType.key].uploaderName || 'N/A'}</p>
                        <a 
                          href={employee.documents[docType.key].url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="action-btn-secondary"
                        >
                          <FaEye /> View Document
                        </a>
                      </div>
                    ) : (
                      <p className="no-document">No document uploaded</p>
                    )}
                    <label className="upload-btn">
                      <FaUpload /> {employee.documents?.[docType.key] ? 'Replace' : 'Upload'}
                      <input
                        type="file"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            onUploadDocument(employee.id, docType.key, e.target.files[0]);
                          }
                        }}
                        disabled={uploading}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Drive Links Tab */}
          {activeTab === 'links' && (
            <div className="tab-content">
              <h3>Google Drive Links</h3>
              
              <div className="drive-links-section">
                <h4>Existing Links:</h4>
                {employee.driveLinks && Object.keys(employee.driveLinks).length > 0 ? (
                  <div className="links-list">
                    {Object.entries(employee.driveLinks).map(([key, url]) => (
                      <div key={key} className="link-item">
                        <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong>
                        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No drive links added yet</p>
                )}
              </div>

              <div className="add-link-section">
                <h4>Add/Update Drive Link:</h4>
                <select 
                  value={driveLinkType} 
                  onChange={(e) => setDriveLinkType(e.target.value)}
                  className="link-type-select"
                >
                  <option value="personalFolder">Personal Folder</option>
                  <option value="projectFolder">Project Folder</option>
                  <option value="notesFolder">Notes Folder</option>
                  <option value="recordingsFolder">Recordings Folder</option>
                  <option value="documentsFolder">Documents Folder</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="url"
                  placeholder="Paste Google Drive link here..."
                  value={driveLinkUrl}
                  onChange={(e) => setDriveLinkUrl(e.target.value)}
                  className="link-input"
                />
                <button
                  onClick={() => {
                    onUpdateDriveLink(employee.id, driveLinkType, driveLinkUrl);
                    setDriveLinkUrl('');
                  }}
                  className="action-btn-primary"
                  disabled={!driveLinkUrl.trim()}
                >
                  <FaLink /> Save Link
                </button>
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="tab-content">
              <h3>Internal Notes</h3>
              
              <div className="notes-list">
                {employee.notes && employee.notes.length > 0 ? (
                  employee.notes.map((note, index) => (
                    <div key={index} className="note-item">
                      <div className="note-header">
                        <strong>{note.addedByName}</strong>
                        <span className="note-date">{new Date(note.addedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' (IST)'}</span>
                      </div>
                      <p className="note-text">{note.text}</p>
                    </div>
                  ))
                ) : (
                  <p>No notes added yet</p>
                )}
              </div>

              <div className="add-note-section">
                <h4>Add New Note:</h4>
                <textarea
                  placeholder="Enter note about this employee..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={4}
                />
                <button
                  onClick={() => {
                    onAddNote(employee.id, newNote);
                    setNewNote('');
                  }}
                  className="action-btn-primary"
                  disabled={!newNote.trim()}
                >
                  Add Note
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeList;
